import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarMonthRounded,
  RestoreRounded,
  TuneRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import IndustrialWorkflowDatePicker, {
  toDateOnlyInputValue,
} from './IndustrialWorkflowDatePicker';
import {
  adjustIndustrialWorkflowStageDeadline,
  previewIndustrialWorkflowStageDeadline,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowCase,
  IndustrialWorkflowStageDeadlineAdjustmentResult,
  IndustrialWorkflowStageDeadlinePropagationMode,
} from '../types/industrialWorkflows';

type Props = {
  workflowCase: IndustrialWorkflowCase | null;
  open: boolean;
  onClose: () => void;
  onAdjusted: (
    result:
      IndustrialWorkflowStageDeadlineAdjustmentResult,
  ) => Promise<void> | void;
};

const PROPAGATION_OPTIONS: Array<{
  value: IndustrialWorkflowStageDeadlinePropagationMode;
  label: string;
  description: string;
}> = [
  {
    value: 'keep_current',
    label: 'Mantener las fechas actuales de las tareas',
    description:
      'Modifica únicamente el plazo de la etapa. Los conflictos que permanezcan quedarán visibles.',
  },
  {
    value: 'clamp_exceeding',
    label: 'Ajustar las tareas que excedan el nuevo plazo',
    description:
      'Recorta únicamente las tareas activas cuyo vencimiento quede fuera del nuevo límite.',
  },
  {
    value: 'manual',
    label: 'Replanificar manualmente',
    description:
      'Permite revisar individualmente la fecha de cada tarea activa antes de guardar.',
  },
  {
    value: 'restore_default',
    label: 'Restablecer el plazo estándar de esta entrada',
    description:
      'Recupera el snapshot calculado cuando el expediente entró en la etapa.',
  },
];

function getErrorMessage(
  error: unknown,
) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function formatDateOnly(
  value: string | null | undefined,
) {
  if (!value) {
    return 'Sin fecha';
  }

  const parsedDate = new Date(
    `${value.slice(0, 10)}T12:00:00`,
  );

  if (Number.isNaN(parsedDate.getTime())) {
    return 'Fecha no disponible';
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'short',
    },
  ).format(parsedDate);
}

export default function IndustrialWorkflowStageDeadlineDialog({
  workflowCase,
  open,
  onClose,
  onAdjusted,
}: Props) {
  const { enqueueSnackbar } =
    useSnackbar();

  const [
    newDueOn,
    setNewDueOn,
  ] = useState('');

  const [
    reason,
    setReason,
  ] = useState('');

  const [
    propagationMode,
    setPropagationMode,
  ] =
    useState<IndustrialWorkflowStageDeadlinePropagationMode>(
      'keep_current',
    );

  const [
    manualDueOnByTaskId,
    setManualDueOnByTaskId,
  ] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      return;
    }

    setNewDueOn(
      toDateOnlyInputValue(
        workflowCase?.current_stage_due_at,
      ),
    );

    setReason('');
    setPropagationMode('keep_current');
    setManualDueOnByTaskId({});
  }, [
    open,
    workflowCase?.current_stage_due_at,
    workflowCase?.id,
  ]);

  const previewQuery =
    useQuery({
      queryKey: [
        'industrial-workflows-stage-deadline-preview',
        workflowCase?.id,
        newDueOn,
      ],
      queryFn: () =>
        previewIndustrialWorkflowStageDeadline(
          workflowCase?.id as string,
          {
            new_due_on:
              newDueOn || null,
          },
        ),
      enabled: Boolean(
        open
        && workflowCase?.id,
      ),
      retry: false,
    });

  useEffect(() => {
    const activeTasks =
      previewQuery.data
        ?.active_tasks
      ?? [];

    setManualDueOnByTaskId(
      (current) => {
        const next = {
          ...current,
        };

        activeTasks.forEach(
          (task) => {
            if (
              !(task.task_id in next)
            ) {
              next[task.task_id] =
                task.due_on ?? '';
            }
          },
        );

        return next;
      },
    );
  }, [
    previewQuery.data
      ?.active_tasks,
  ]);

  const summary =
    previewQuery.data?.summary;

  const shouldHighlightReason =
    Boolean(
      previewQuery.data
      && (
        (newDueOn || null)
        !== (
          previewQuery.data
            .current_due_on
          ?? null
        )
      )
      && !reason.trim(),
    );

  const tasksAfterStageDue =
    previewQuery.data
      ?.conflicts
      .tasks_after_new_stage_due
    ?? [];

  const blockingTasksAfterStageDue =
    previewQuery.data
      ?.conflicts
      .blocking_tasks_after_new_stage_due
    ?? [];

  const hasWarnings =
    Boolean(
      previewQuery.data
        ?.conflicts
        .stage_after_case_target,
    )
    || tasksAfterStageDue.length > 0
    || Boolean(
      summary?.task_without_due_count,
    );

  const taskAdjustments =
    useMemo(
      () =>
        (
          previewQuery.data
            ?.active_tasks
          ?? []
        ).map((task) => ({
          task_id: task.task_id,
          new_due_on:
            manualDueOnByTaskId[
              task.task_id
            ]
            || null,
        })),
      [
        manualDueOnByTaskId,
        previewQuery.data
          ?.active_tasks,
      ],
    );

  const adjustMutation =
    useMutation({
      mutationFn: () =>
        adjustIndustrialWorkflowStageDeadline(
          workflowCase?.id as string,
          {
            expected_stage_entry_token:
              workflowCase?.current_stage_entry_token as string,

            new_due_on:
              newDueOn || null,

            reason:
              reason.trim(),

            propagation_mode:
              propagationMode,

            task_adjustments:
              propagationMode
                === 'manual'
                ? taskAdjustments
                : [],
          },
        ),
      onSuccess: async (
        result,
      ) => {
        await onAdjusted(
          result,
        );

        enqueueSnackbar(
          'Plazo de etapa actualizado correctamente.',
          {
            variant: 'success',
          },
        );

        onClose();
      },
      onError: (error) => {
        enqueueSnackbar(
          getErrorMessage(error),
          {
            variant: 'error',
          },
        );
      },
    });

  const handlePropagationModeChange = (
    value:
      IndustrialWorkflowStageDeadlinePropagationMode,
  ) => {
    setPropagationMode(value);

    if (
      value === 'restore_default'
    ) {
      setNewDueOn(
        previewQuery.data
          ?.default_due_on
        ?? '',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={
        adjustMutation.isPending
          ? undefined
          : onClose
      }
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Ajustar plazo de etapa
      </DialogTitle>

      <DialogContent>
        <Stack
          spacing={1.4}
          sx={{ pt: 1 }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(
                '#38bdf8',
                0.2,
              )}`,
              bgcolor:
                'rgba(15,23,42,0.5)',
              px: 1.35,
              py: 1.1,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                  }}
                >
                  {workflowCase
                    ?.reference_code
                    ?? 'Expediente'}
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.8rem',
                    mt: 0.18,
                  }}
                >
                  El cambio se aplicará únicamente a
                  la entrada de etapa vigente.
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={0.55}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  label={`Estándar · ${formatDateOnly(
                    previewQuery.data
                      ?.default_due_on,
                  )}`}
                />

                <Chip
                  size="small"
                  label={`Vigente · ${formatDateOnly(
                    previewQuery.data
                      ?.current_due_on,
                  )}`}
                  sx={{
                    color: '#bae6fd',
                    bgcolor:
                      alpha(
                        '#0284c7',
                        0.16,
                      ),
                  }}
                />
              </Stack>
            </Stack>
          </Paper>

          <IndustrialWorkflowDatePicker
            label="Nuevo vencimiento de etapa"
            value={newDueOn}
            onChange={setNewDueOn}
            helperText="Fecha operativa vigente para esta entrada concreta en etapa."
          />

          {previewQuery.isFetching ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
            >
              <CircularProgress
                size={18}
              />

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                }}
              >
                Analizando impacto...
              </Typography>
            </Stack>
          ) : null}

          {previewQuery.isError ? (
            <Alert
              severity="error"
              variant="outlined"
            >
              {getErrorMessage(
                previewQuery.error,
              )}
            </Alert>
          ) : null}

          {previewQuery.data ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  hasWarnings
                    ? '#f59e0b'
                    : '#22c55e',
                  0.24,
                )}`,
                bgcolor:
                  'rgba(2,6,23,0.22)',
                px: 1.25,
                py: 1.1,
              }}
            >
              <Stack spacing={0.9}>
                <Stack
                  direction="row"
                  spacing={0.65}
                  alignItems="center"
                >
                  {hasWarnings ? (
                    <WarningAmberRounded
                      sx={{
                        color: '#fbbf24',
                      }}
                    />
                  ) : (
                    <TuneRounded
                      sx={{
                        color: '#4ade80',
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Impacto del cambio
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.55}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    size="small"
                    label={`${summary?.active_task_count ?? 0} tareas activas`}
                  />

                  <Chip
                    size="small"
                    label={`${summary?.task_after_stage_due_count ?? 0} fuera del nuevo plazo`}
                    sx={{
                      color:
                        summary
                          ?.task_after_stage_due_count
                          ? '#fde68a'
                          : '#cbd5e1',
                      bgcolor:
                        summary
                          ?.task_after_stage_due_count
                          ? alpha(
                              '#f59e0b',
                              0.15,
                            )
                          : alpha(
                              '#64748b',
                              0.12,
                            ),
                    }}
                  />

                  <Chip
                    size="small"
                    label={`${summary?.blocking_task_after_stage_due_count ?? 0} bloqueantes fuera de plazo`}
                    sx={{
                      color:
                        summary
                          ?.blocking_task_after_stage_due_count
                          ? '#fecaca'
                          : '#cbd5e1',
                      bgcolor:
                        summary
                          ?.blocking_task_after_stage_due_count
                          ? alpha(
                              '#ef4444',
                              0.16,
                            )
                          : alpha(
                              '#64748b',
                              0.12,
                            ),
                    }}
                  />

                  <Chip
                    size="small"
                    label={`${summary?.task_without_due_count ?? 0} sin fecha`}
                  />
                </Stack>

                {previewQuery.data
                  .conflicts
                  .stage_after_case_target ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                  >
                    El nuevo vencimiento de etapa queda
                    después del objetivo global del expediente.
                  </Alert>
                ) : null}

                {tasksAfterStageDue.length ? (
                  <Stack spacing={0.55}>
                    <Typography
                      sx={{
                        color: '#fde68a',
                        fontWeight: 850,
                        fontSize: '0.8rem',
                      }}
                    >
                      Tareas que exceden el nuevo plazo
                    </Typography>

                    {tasksAfterStageDue.map(
                      (task) => (
                        <Stack
                          key={task.task_id}
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{
                            borderRadius: 2,
                            bgcolor:
                              'rgba(15,23,42,0.46)',
                            px: 0.9,
                            py: 0.65,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#e2e8f0',
                              fontSize: '0.8rem',
                              fontWeight: 800,
                            }}
                          >
                            {task.title}
                          </Typography>

                          <Chip
                            size="small"
                            label={formatDateOnly(
                              task.due_on,
                            )}
                            sx={{
                              color:
                                task.is_blocking
                                  ? '#fecaca'
                                  : '#fde68a',
                              bgcolor:
                                task.is_blocking
                                  ? alpha(
                                      '#ef4444',
                                      0.16,
                                    )
                                  : alpha(
                                      '#f59e0b',
                                      0.15,
                                    ),
                            }}
                          />
                        </Stack>
                      ),
                    )}
                  </Stack>
                ) : null}

                {blockingTasksAfterStageDue.length ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                  >
                    Existen tareas bloqueantes después del
                    nuevo límite. Selecciona una estrategia
                    explícita antes de guardar.
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
          ) : null}

          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
                mb: 0.7,
              }}
            >
              Estrategia de replanificación
            </Typography>

            <RadioGroup
              value={propagationMode}
              onChange={(event) =>
                handlePropagationModeChange(
                  event.target.value as
                    IndustrialWorkflowStageDeadlinePropagationMode,
                )
              }
            >
              <Stack spacing={0.65}>
                {PROPAGATION_OPTIONS.map(
                  (option) => (
                    <Paper
                      key={option.value}
                      elevation={0}
                      sx={{
                        borderRadius: 2.3,
                        border: `1px solid ${alpha(
                          propagationMode
                            === option.value
                            ? '#38bdf8'
                            : '#94a3b8',
                          propagationMode
                            === option.value
                            ? 0.34
                            : 0.14,
                        )}`,
                        bgcolor:
                          propagationMode
                          === option.value
                            ? alpha(
                                '#0284c7',
                                0.1,
                              )
                            : 'rgba(15,23,42,0.34)',
                        px: 0.85,
                        py: 0.55,
                      }}
                    >
                      <FormControlLabel
                        value={option.value}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 850,
                                fontSize: '0.86rem',
                              }}
                            >
                              {option.label}
                            </Typography>

                            <Typography
                              sx={{
                                color: '#94a3b8',
                                fontSize: '0.75rem',
                                mt: 0.12,
                              }}
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          alignItems:
                            'flex-start',
                          m: 0,
                        }}
                      />
                    </Paper>
                  ),
                )}
              </Stack>
            </RadioGroup>
          </Box>

          {propagationMode === 'manual'
          && previewQuery.data
            ?.active_tasks
            .length ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#a78bfa',
                  0.24,
                )}`,
                bgcolor:
                  alpha(
                    '#312e81',
                    0.1,
                  ),
                px: 1.15,
                py: 1.05,
              }}
            >
              <Stack spacing={0.85}>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Fechas individuales de tareas
                </Typography>

                {previewQuery.data
                  .active_tasks
                  .map((task) => (
                    <Stack
                      key={task.task_id}
                      direction={{
                        xs: 'column',
                        md: 'row',
                      }}
                      spacing={0.8}
                      alignItems={{
                        xs: 'stretch',
                        md: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#e2e8f0',
                            fontWeight: 850,
                            fontSize: '0.84rem',
                          }}
                        >
                          {task.title}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.73rem',
                            mt: 0.1,
                          }}
                        >
                          {task.is_blocking
                            ? 'Tarea bloqueante'
                            : 'Tarea no bloqueante'}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: {
                            xs: '100%',
                            md: 220,
                          },
                        }}
                      >
                        <IndustrialWorkflowDatePicker
                          size="small"
                          label="Nuevo vencimiento"
                          value={
                            manualDueOnByTaskId[
                              task.task_id
                            ]
                            ?? ''
                          }
                          onChange={(value) =>
                            setManualDueOnByTaskId(
                              (current) => ({
                                ...current,
                                [task.task_id]:
                                  value,
                              }),
                            )
                          }
                        />
                      </Box>
                    </Stack>
                  ))}
              </Stack>
            </Paper>
          ) : null}

          <TextField
            label="Motivo del ajuste"
            value={reason}
            onChange={(event) =>
              setReason(
                event.target.value,
              )
            }
            multiline
            minRows={3}
            error={shouldHighlightReason}
            helperText={
              shouldHighlightReason
                ? 'Indica el motivo del ajuste para poder guardar el nuevo plazo.'
                : 'Obligatorio. El motivo quedará registrado en la actividad del expediente.'
            }
            fullWidth
            required
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={
            adjustMutation.isPending
          }
          onClick={onClose}
        >
          Cancelar
        </Button>

        <Button
          variant="contained"
          startIcon={
            propagationMode
            === 'restore_default'
              ? <RestoreRounded />
              : <CalendarMonthRounded />
          }
          disabled={
            adjustMutation.isPending
            || previewQuery.isFetching
            || previewQuery.isError
            || !workflowCase
              ?.current_stage_entry_token
            || !reason.trim()
          }
          onClick={() =>
            adjustMutation.mutate()
          }
        >
          Guardar ajuste
        </Button>
      </DialogActions>
    </Dialog>
  );
}